import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getLicenses, deleteLicense, downloadExport, importLicenses, getImportTemplate } from '../services/api';
import DatePicker from '../components/DatePicker';
import { useAuth } from '../contexts/AuthContext';
import LicenseFormDialog from '../components/LicenseFormDialog';
import LicenseUpdateDialog from '../components/LicenseUpdateDialog';
import AIReviewDialog from '../components/AIReviewDialog';
import Pagination from '../components/Pagination';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Eye, 
  Pencil, 
  Trash2,
  FileText,
  Calendar,
  Building2,
  X,
  Factory,
  Users,
  Download,
  Upload,
  FileSpreadsheet,
  FileDown,
  ChevronDown,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const formatAttachDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const Licenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [areaFilter, setAreaFilter] = useState(searchParams.get('area') || 'all');
  const [criticalFilter, setCriticalFilter] = useState(searchParams.get('is_critical') === 'true');
  const [expiryStart, setExpiryStart] = useState('');
  const [expiryEnd, setExpiryEnd] = useState('');
  const [issueStart, setIssueStart] = useState('');
  const [issueEnd, setIssueEnd] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSheet, setImportSheet] = useState(null);
  const [importAttachments, setImportAttachments] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingLicenseId, setEditingLicenseId] = useState(null);
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [knPage, setKnPage] = useState(1);
  const [fornPage, setFornPage] = useState(1);
  const PAGE_SIZE = 10;
  const fileInputRef = useRef(null);
  
  const { isGestor, hasPermission } = useAuth();

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (criticalFilter) params.is_critical = true;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (areaFilter && areaFilter !== 'all') params.area = areaFilter;
      if (search) params.search = search;
      
      const response = await getLicenses(params);
      setLicenses(response.data);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      toast.error('Erro ao carregar licenças');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, categoryFilter, areaFilter, criticalFilter]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchLicenses();
    }, 300);
    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (areaFilter !== 'all') params.set('area', areaFilter);
    if (search) params.set('search', search);
    setSearchParams(params);
  }, [typeFilter, statusFilter, categoryFilter, areaFilter, search, setSearchParams]);

  const handleDelete = async () => {
    if (!licenseToDelete) return;
    try {
      await deleteLicense(licenseToDelete.id);
      toast.success('Licença excluída com sucesso');
      fetchLicenses();
    } catch (error) {
      toast.error('Erro ao excluir licença');
    } finally {
      setDeleteDialogOpen(false);
      setLicenseToDelete(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setAreaFilter('all');
    setCriticalFilter(false);
    setExpiryStart('');
    setExpiryEnd('');
    setIssueStart('');
    setIssueEnd('');
    setSearchParams({});
  };

  const getExportParams = () => {
    const params = {};
    if (typeFilter !== 'all') params.type = typeFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (expiryStart) params.expiry_start = expiryStart;
    if (expiryEnd) params.expiry_end = expiryEnd;
    if (issueStart) params.issue_start = issueStart;
    if (issueEnd) params.issue_end = issueEnd;
    return params;
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Gerando Excel...');
      const response = await downloadExport('excel', getExportParams());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `licencas_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      const response = await downloadExport('pdf', getExportParams());
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `licencas_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleImport = async () => {
    if (!importSheet) {
      toast.error('Selecione a planilha (.xlsx ou .csv)');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importLicenses(importSheet, importAttachments);
      const data = result.data;
      setImportResult(data);
      if (data.success > 0) {
        toast.success(`${data.success} licença(s) importada(s)${data.attachments_linked ? ` · ${data.attachments_linked} anexo(s)` : ''}`);
      }
      if (data.errors && data.errors.length > 0) {
        toast.error(`${data.errors.length} erro(s) na importação`);
      }
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportSheet(null);
    setImportAttachments([]);
    setImportResult(null);
  };

  const getStatusBadge = (status) => {
    const config = {
      ativa: { label: 'Ativa', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      proxima_vencimento: { label: 'A Vencer', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      vencida: { label: 'Vencida', className: 'bg-rose-50 text-rose-700 border-rose-200' }
    };
    const { label, className } = config[status] || config.ativa;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const config = {
      ae: { label: 'AE', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      afe: { label: 'AFE', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      alvara: { label: 'Alvará', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      antt: { label: 'ANTT', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      avcb: { label: 'AVCB', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      certidao_crf: { label: 'Cert. CRF', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      certidao_federal: { label: 'Cert. Federal', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      cetesb: { label: 'CETESB', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      cnpj: { label: 'CNPJ', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      marinha_mercante: { label: 'Marinha Mercante', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      crq: { label: 'CRQ', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      licenca_ambiental: { label: 'Lic. Ambiental', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      policia_civil: { label: 'Polícia Civil', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      policia_federal: { label: 'Polícia Federal', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      exercito: { label: 'Exército', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      licenca_sanitaria: { label: 'Lic. Sanitária', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      vre: { label: 'VRE', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      // Legacy types for backward compatibility
      ambiental: { label: 'Ambiental', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      sanitaria: { label: 'Sanitária', className: 'bg-gray-100 text-gray-700 border-gray-200' }
    };
    const { label, className } = config[type] || { label: type, className: 'bg-gray-100 text-gray-600 border-gray-200' };
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const config = {
      kn: { label: 'Kuehne+Nagel', className: 'bg-blue-50 text-[#003366] border-blue-200', icon: Factory },
      fornecedor: { label: 'Fornecedor', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: Users }
    };
    const { label, className, icon: Icon } = config[category] || config.kn;
    return (
      <Badge variant="outline" className={`${className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const hasActiveFilters = search || typeFilter !== 'all' || statusFilter !== 'all' || categoryFilter !== 'all' || areaFilter !== 'all' || criticalFilter || expiryStart || expiryEnd || issueStart || issueEnd;

  const openCreateDialog = () => { setEditingLicenseId(null); setFormDialogOpen(true); };
  const openEditDialog = (id) => { setEditingLicenseId(id); setFormDialogOpen(true); };
  const handleFormSuccess = () => { fetchLicenses(); };
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updatingLicense, setUpdatingLicense] = useState(null);
  const openUpdateDialog = (license) => { setUpdatingLicense(license); setUpdateDialogOpen(true); };

  const knLicenses = licenses.filter(l => l.category === 'kn');
  const fornecedorLicenses = licenses.filter(l => l.category === 'fornecedor');

  useEffect(() => { setKnPage(1); setFornPage(1); }, [licenses]);

  const knPageItems = knLicenses.slice((knPage - 1) * PAGE_SIZE, knPage * PAGE_SIZE);
  const fornPageItems = fornecedorLicenses.slice((fornPage - 1) * PAGE_SIZE, fornPage * PAGE_SIZE);

  const renderLicenseRow = (license) => (
    <tr 
      key={license.id} 
      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
      data-testid={`license-row-${license.id}`}
    >
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FileText className="w-4 h-4 text-[#003366]" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{license.name}</p>
            {license.responsible_person && (
              <p className="text-xs text-gray-500">{license.responsible_person}</p>
            )}
          </div>
        </div>
      </td>
      <td className="p-4">{getTypeBadge(license.type)}</td>
      <td className="p-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Building2 className="w-4 h-4 text-gray-400" />
          {license.issuing_authority}
        </div>
      </td>
      <td className="p-4">
        <code className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
          {license.license_number}
        </code>
      </td>
      <td className="p-4">
        {license.protocol_number ? (
          <span className="text-sm text-gray-700 font-mono" data-testid={`protocol-${license.id}`}>{license.protocol_number}</span>
        ) : (
          <span className="text-gray-300 text-sm">—</span>
        )}
      </td>
      <td className="p-4">
        {(license.attachments || []).length > 0 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-1.5 text-[#003366] hover:bg-blue-50 px-2 py-1 rounded-md transition-colors text-sm font-semibold"
                data-testid={`attachments-btn-${license.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Paperclip className="w-4 h-4" />
                {license.attachments.length}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-white border-gray-200 p-2" align="start">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                Anexos ({license.attachments.length})
              </p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {license.attachments.map((att) => (
                  <a
                    key={att.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={att.file_name}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors"
                    data-testid={`attachment-popover-item-${att.id}`}
                  >
                    <FileText className="w-4 h-4 text-[#003366] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{att.file_name}</p>
                      <p className="text-xs text-gray-500">{formatAttachDate(att.uploaded_at)}</p>
                    </div>
                    <Download className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="text-gray-300 text-sm pl-2">—</span>
        )}
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Calendar className="w-4 h-4 text-gray-400" />
          {license.indeterminate_expiry ? 'Indeterminado' : formatDate(license.expiry_date)}
        </div>
      </td>
      <td className="p-4">{getStatusBadge(license.status)}</td>
      <td className="p-4 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700 hover:bg-gray-100" data-testid={`license-actions-${license.id}`}>
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border-gray-200">
            <DropdownMenuItem asChild className="text-gray-700 focus:bg-gray-100 cursor-pointer">
              <Link to={`/licenses/${license.id}`} data-testid={`view-license-${license.id}`}>
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </Link>
            </DropdownMenuItem>
            {license.file_url && (
              <DropdownMenuItem asChild className="text-gray-700 focus:bg-gray-100 cursor-pointer">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${license.file_url}`} target="_blank" rel="noopener noreferrer" data-testid={`download-license-${license.id}`}>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Arquivo
                </a>
              </DropdownMenuItem>
            )}
            {hasPermission('licenses', 'edit') && (
              <DropdownMenuItem className="text-gray-700 focus:bg-gray-100 cursor-pointer" onClick={() => openEditDialog(license.id)} data-testid={`edit-license-${license.id}`}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {hasPermission('licenses', 'edit') && (
              <DropdownMenuItem className="text-gray-700 focus:bg-gray-100 cursor-pointer" onClick={() => openUpdateDialog(license)} data-testid={`update-license-${license.id}`}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </DropdownMenuItem>
            )}
            {hasPermission('licenses', 'delete') && (
              <DropdownMenuItem className="text-rose-600 focus:bg-rose-50 cursor-pointer" onClick={() => { setLicenseToDelete(license); setDeleteDialogOpen(true); }} data-testid={`delete-license-${license.id}`}>
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );

  const renderTable = (items, testId) => (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid={testId}>
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Licença</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Tipo</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Órgão Emissor</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CNPJ</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Nº Protocolo</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Vencimento</th>
            <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
            <th className="text-right p-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map(renderLicenseRow)}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="licenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Licenças e Alvarás</h1>
          <p className="text-gray-500 mt-1">Gerencie todas as suas licenças regulatórias</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export/Import Dropdown */}
          {hasPermission('export_import', 'use') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="export-import-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar / Importar
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-gray-200 w-56">
              <DropdownMenuItem onClick={handleExportExcel} className="cursor-pointer text-gray-700 hover:bg-gray-50">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Exportar Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF} className="cursor-pointer text-gray-700 hover:bg-gray-50">
                <FileDown className="w-4 h-4 mr-2 text-red-600" />
                Exportar PDF
              </DropdownMenuItem>
              {hasPermission('export_import', 'use') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setImportDialogOpen(true)} className="cursor-pointer text-gray-700 hover:bg-gray-50">
                    <Upload className="w-4 h-4 mr-2 text-blue-600" />
                    Importar Licenças
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer text-gray-700 hover:bg-gray-50">
                    <a href={getImportTemplate()} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2 text-gray-500" />
                      Baixar Template
                    </a>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          
          {hasPermission('licenses', 'edit') && (
            <Button onClick={() => setAiReviewOpen(true)} variant="outline" className="bg-white border-[#003366]/30 text-[#003366] hover:bg-blue-50 font-semibold" data-testid="ai-review-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Validar com IA
            </Button>
          )}

          {hasPermission('licenses', 'create') && (
            <Button onClick={openCreateDialog} className="bg-[#003366] hover:bg-[#002244] font-semibold" data-testid="new-license-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Licença
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, CNPJ, órgão ou protocolo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  data-testid="search-input"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[160px] bg-white border-gray-300 text-gray-700" data-testid="category-filter">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all" className="text-gray-700">Todas categorias</SelectItem>
                    <SelectItem value="kn" className="text-gray-700">Kuehne+Nagel</SelectItem>
                    <SelectItem value="fornecedor" className="text-gray-700">Fornecedor</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={areaFilter} onValueChange={setAreaFilter}>
                  <SelectTrigger className="w-[200px] bg-white border-gray-300 text-gray-700" data-testid="area-filter">
                    <SelectValue placeholder="Área" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all" className="text-gray-700">Todas as áreas</SelectItem>
                    <SelectItem value="fiscal" className="text-gray-700">Fiscal</SelectItem>
                    <SelectItem value="juridico" className="text-gray-700">Jurídico</SelectItem>
                    <SelectItem value="licenciamento" className="text-gray-700">Licenciamento/Regulatório</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-700" data-testid="type-filter">
                    <Filter className="w-4 h-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                  <SelectItem value="all" className="text-gray-700">Todos os tipos</SelectItem>
                  <SelectItem value="ae" className="text-gray-700">AE</SelectItem>
                  <SelectItem value="afe" className="text-gray-700">AFE</SelectItem>
                  <SelectItem value="alvara" className="text-gray-700">Alvará</SelectItem>
                  <SelectItem value="antt" className="text-gray-700">ANTT</SelectItem>
                  <SelectItem value="avcb" className="text-gray-700">AVCB</SelectItem>
                  <SelectItem value="certidao_crf" className="text-gray-700">Cert. CRF</SelectItem>
                  <SelectItem value="certidao_federal" className="text-gray-700">Cert. Federal</SelectItem>
                  <SelectItem value="cetesb" className="text-gray-700">CETESB</SelectItem>
                  <SelectItem value="cnpj" className="text-gray-700">CNPJ</SelectItem>
                  <SelectItem value="marinha_mercante" className="text-gray-700">Marinha Mercante</SelectItem>
                  <SelectItem value="crq" className="text-gray-700">CRQ</SelectItem>
                  <SelectItem value="licenca_ambiental" className="text-gray-700">Lic. Ambiental</SelectItem>
                  <SelectItem value="policia_civil" className="text-gray-700">Polícia Civil</SelectItem>
                  <SelectItem value="policia_federal" className="text-gray-700">Polícia Federal</SelectItem>
                  <SelectItem value="exercito" className="text-gray-700">Exército</SelectItem>
                  <SelectItem value="licenca_sanitaria" className="text-gray-700">Lic. Sanitária</SelectItem>
                  <SelectItem value="vre" className="text-gray-700">VRE</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-300 text-gray-700" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all" className="text-gray-700">Todos os status</SelectItem>
                  <SelectItem value="ativa" className="text-gray-700">Ativa</SelectItem>
                  <SelectItem value="proxima_vencimento" className="text-gray-700">A Vencer</SelectItem>
                  <SelectItem value="vencida" className="text-gray-700">Vencida</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => setShowDateFilters(!showDateFilters)}
                className={`bg-white border-gray-300 text-gray-700 hover:bg-gray-50 ${showDateFilters ? 'bg-gray-100' : ''}`}
                data-testid="date-filters-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Datas
              </Button>

              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              )}
              </div>
            </div>
            
            {/* Date Filters */}
            {showDateFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">Vencimento (de)</Label>
                  <DatePicker
                    value={expiryStart}
                    onChange={setExpiryStart}
                    data-testid="expiry-start-input"
                    placeholder="Data inicial"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">Vencimento (até)</Label>
                  <DatePicker
                    value={expiryEnd}
                    onChange={setExpiryEnd}
                    data-testid="expiry-end-input"
                    placeholder="Data final"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">Emissão (de)</Label>
                  <DatePicker
                    value={issueStart}
                    onChange={setIssueStart}
                    data-testid="issue-start-input"
                    placeholder="Data inicial"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">Emissão (até)</Label>
                  <DatePicker
                    value={issueEnd}
                    onChange={setIssueEnd}
                    data-testid="issue-end-input"
                    placeholder="Data final"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* License Tables by Category */}
      {loading ? (
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="bg-white border-gray-200">
              <div className="p-6 space-y-4">
                <Skeleton className="h-8 w-48 bg-gray-100" />
                {[...Array(3)].map((_, j) => (
                  <Skeleton key={j} className="h-14 bg-gray-100" />
                ))}
              </div>
            </Card>
          ))}
        </div>
      ) : licenses.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <div className="text-center py-16" data-testid="empty-state">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhuma licença encontrada</h3>
            <p className="text-gray-500 mb-6">
              {hasActiveFilters 
                ? 'Tente ajustar os filtros de busca' 
                : 'Comece adicionando sua primeira licença'}
            </p>
            {hasPermission('licenses', 'create') && !hasActiveFilters && (
              <Button onClick={openCreateDialog} className="bg-[#003366] hover:bg-[#002244]">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Licença
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Kuehne+Nagel Section */}
          <Card className="bg-white border-gray-200 overflow-hidden shadow-md" data-testid="kn-section">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#003366]/5 to-transparent">
              <div className="p-2 bg-[#003366]/10 rounded-lg">
                <Factory className="w-5 h-5 text-[#003366]" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-[#003366]">Kuehne+Nagel</h2>
                <p className="text-sm text-gray-500">Licenças próprias da empresa</p>
              </div>
              <Badge variant="outline" className="bg-blue-50 text-[#003366] border-blue-200 text-sm font-semibold">
                {knLicenses.length} {knLicenses.length === 1 ? 'licença' : 'licenças'}
              </Badge>
            </div>
            {knLicenses.length > 0 ? (
              <>
                {renderTable(knPageItems, 'kn-licenses-table')}
                <Pagination page={knPage} pageSize={PAGE_SIZE} total={knLicenses.length} onPageChange={setKnPage} testId="kn-pagination" />
              </>
            ) : (
              <div className="text-center py-10 text-gray-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma licença Kuehne+Nagel encontrada</p>
              </div>
            )}
          </Card>

          {/* Fornecedor Section */}
          <Card className="bg-white border-gray-200 overflow-hidden shadow-md" data-testid="fornecedor-section">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-100/80 to-transparent">
              <div className="p-2 bg-gray-200/60 rounded-lg">
                <Users className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800">Fornecedores</h2>
                <p className="text-sm text-gray-500">Licenças de terceiros e fornecedores</p>
              </div>
              <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-sm font-semibold">
                {fornecedorLicenses.length} {fornecedorLicenses.length === 1 ? 'licença' : 'licenças'}
              </Badge>
            </div>
            {fornecedorLicenses.length > 0 ? (
              <>
                {renderTable(fornPageItems, 'fornecedor-licenses-table')}
                <Pagination page={fornPage} pageSize={PAGE_SIZE} total={fornecedorLicenses.length} onPageChange={setFornPage} testId="fornecedor-pagination" />
              </>
            ) : (
              <div className="text-center py-10 text-gray-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma licença de fornecedor encontrada</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Tem certeza que deseja excluir a licença &ldquo;{licenseToDelete?.name}&rdquo;? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(v) => { setImportDialogOpen(v); if (!v) resetImport(); }}>
        <DialogContent className="bg-white border-gray-200 sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Importar Licenças</DialogTitle>
            <DialogDescription className="text-gray-500">
              Envie a planilha (.xlsx ou .csv) e, opcionalmente, os arquivos anexos. Preencha a coluna <strong>&quot;Arquivo&quot;</strong> com o nome de cada anexo para vinculá-lo automaticamente.
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div className="space-y-4 py-2">
              {/* Template */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm text-blue-800 font-medium">Precisa de um modelo?</p>
                  <p className="text-xs text-blue-600">Template com a coluna &quot;Arquivo&quot; incluída</p>
                </div>
                <a href={getImportTemplate()} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-testid="import-template-link">
                  Baixar
                </a>
              </div>

              {/* Spreadsheet */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">1. Planilha *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setImportSheet(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-[#003366] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#002244] cursor-pointer border border-gray-300 rounded-lg"
                  data-testid="import-file-input"
                />
                {importSheet && <p className="text-xs text-gray-500">Selecionado: {importSheet.name}</p>}
              </div>

              {/* Attachments */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">2. Arquivos anexos (opcional)</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setImportAttachments(Array.from(e.target.files || []))}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-200 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-300 cursor-pointer border border-gray-300 rounded-lg"
                  data-testid="import-attachments-input"
                />
                {importAttachments.length > 0 && (
                  <p className="text-xs text-gray-500">{importAttachments.length} arquivo(s): {importAttachments.map(f => f.name).join(', ')}</p>
                )}
                <p className="text-xs text-gray-400">PDF, DOC, JPG, PNG (máx. 10MB cada). O nome deve bater com a coluna &quot;Arquivo&quot; da planilha.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2" data-testid="import-result">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700" data-testid="import-result-created">
                  <CheckCircle2 className="w-4 h-4" /><span className="text-sm font-medium">Criadas:</span><span className="text-sm font-bold">{importResult.success}</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[#003366]" data-testid="import-result-attachments">
                  <Paperclip className="w-4 h-4" /><span className="text-sm font-medium">Anexos:</span><span className="text-sm font-bold">{importResult.attachments_linked || 0}</span>
                </div>
              </div>
              {importResult.attachments_missing?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Arquivos não encontrados ({importResult.attachments_missing.length})</p>
                  <p className="text-xs text-amber-700">{importResult.attachments_missing.join(', ')}</p>
                </div>
              )}
              {importResult.files_unused?.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Arquivos enviados sem correspondência ({importResult.files_unused.length})</p>
                  <p className="text-xs text-gray-500">{importResult.files_unused.join(', ')}</p>
                </div>
              )}
              {importResult.errors?.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <p className="text-sm font-semibold text-rose-800 mb-1">Erros ({importResult.errors.length})</p>
                  <ul className="text-xs text-rose-700 space-y-0.5 max-h-28 overflow-y-auto">
                    {importResult.errors.map((er, i) => (<li key={i}>{er}</li>))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!importResult ? (
              <>
                <Button variant="outline" onClick={() => { setImportDialogOpen(false); resetImport(); }} className="bg-white border-gray-300 text-gray-700">
                  Cancelar
                </Button>
                <Button onClick={handleImport} disabled={importing || !importSheet} className="bg-[#003366] hover:bg-[#002244] text-white font-semibold" data-testid="import-submit-btn">
                  {importing ? 'Importando...' : 'Importar'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={resetImport} className="bg-white border-gray-300 text-gray-700" data-testid="import-again-btn">Importar outra</Button>
                <Button onClick={() => { setImportDialogOpen(false); resetImport(); }} className="bg-[#003366] hover:bg-[#002244] text-white" data-testid="import-done-btn">Concluir</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* License Create/Edit Dialog */}
      <LicenseFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        licenseId={editingLicenseId}
        onSuccess={handleFormSuccess}
      />

      <LicenseUpdateDialog
        open={updateDialogOpen}
        onOpenChange={setUpdateDialogOpen}
        license={updatingLicense}
        onSuccess={handleFormSuccess}
      />

      <AIReviewDialog
        open={aiReviewOpen}
        onOpenChange={setAiReviewOpen}
        defaultCategory={categoryFilter}
        onApplied={fetchLicenses}
      />
    </div>
  );
};

export default Licenses;
